import React, { Suspense, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Baby, 
  Award, 
  BookOpen, 
  Users, 
  Calendar,
  ArrowLeft,
  GraduationCap,
  Home,
  Smile,
  Shield,
  HandHeart
} from 'lucide-react';
import { Canvas, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  useGLTF,
  Environment,
  Center,
  Loader,
  Stars,
  PerspectiveCamera
} from "@react-three/drei";

// Préchargement du modèle
useGLTF.preload("/models/baby_boy.glb");

// Modèle 3D optimisé
function BabyBoyModel() {
  const { scene } = useGLTF("/models/baby_boy.glb");
  const ref = useRef();
  
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (ref.current) {
      ref.current.rotation.y = t * 0.15; // Rotation plus lente et douce
    }
  });
  
  return (
    <primitive
      ref={ref}
      object={scene}
      scale={[0.04, 0.04, 0.04]} // Taille modérée
      position={[0, 0, 0]} // Légèrement en bas
    />
  );
}

// Scène 3D optimisée
function Scene3D() {
  return (
    <Canvas
      shadows
      className="fixed inset-0 -z-10"
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: false }}
    >
      <color attach="background" args={["#1e1e2e"]} />
      <fog attach="fog" args={["#1e1e2e", 15, 35]} />
      
      {/* Éclairage optimisé pour bien voir le modèle */}
      <ambientLight intensity={0.5} />
      <directionalLight position={[5, 5, 5]} intensity={1.2} castShadow />
      <pointLight position={[-5, 3, -3]} intensity={0.3} color="#ff69b4" />
      <pointLight position={[5, 0, 3]} intensity={0.3} color="#ffc0cb" />
      <spotLight 
        position={[0, 8, 5]} 
        angle={0.4} 
        penumbra={1} 
        intensity={0.6} 
        castShadow 
      />
      
      <Suspense fallback={null}>
        {/* Caméra positionnée pour une bonne vue */}
        <PerspectiveCamera makeDefault position={[2, 1, 10]} fov={45} />
        
        <Center>
          <BabyBoyModel />
        </Center>
        
        <Environment preset="sunset" />
        <OrbitControls 
          enableZoom={false} 
          enablePan={false} 
          autoRotate 
          autoRotateSpeed={0.3}
          minPolarAngle={Math.PI / 3.5}
          maxPolarAngle={Math.PI / 1.8}
        />
        <Stars count={2500} depth={50} fade speed={0.8} />
      </Suspense>
    </Canvas>
  );
}

const ServicesPersonne = () => {
  const formations = [
    {
      icon: <Baby className="w-8 h-8 text-pink-400" />,
      title: "CAP Accompagnant Éducatif Petite Enfance (AEPE)",
      duration: "2 ans",
      niveau: "Niveau 3 (CAP)",
      description: "Formation aux métiers de la petite enfance pour accompagner le développement des jeunes enfants.",
      competences: [
        "Accompagner le développement du jeune enfant",
        "Exercer son activité en accueil collectif",
        "Exercer son activité en accueil individuel",
        "Assurer l'hygiène et la sécurité des enfants"
      ]
    },
    {
      icon: <HandHeart className="w-8 h-8 text-rose-400" />,
      title: "Titre Professionnel Assistant(e) De Vie aux Familles (ADVF)",
      duration: "9 mois",
      niveau: "Niveau 3 (CAP)",
      description: "Formation professionnelle pour accompagner les personnes dans leur vie quotidienne.",
      competences: [
        "Entretenir le logement et le linge d'un particulier",
        "Accompagner la personne dans ses activités essentielles",
        "Relayer les parents dans la prise en charge de leurs enfants",
        "Assurer la sécurité et le bien-être des personnes"
      ]
    }
  ];

  const debouches = [
    {
      icon: <Baby className="w-6 h-6 text-pink-400" />,
      title: "Agent de crèche",
      description: "Accueillir et accompagner les enfants en crèche"
    },
    {
      icon: <Home className="w-6 h-6 text-rose-400" />,
      title: "Assistant(e) maternel(le)",
      description: "Garder des enfants à domicile"
    },
    {
      icon: <Users className="w-6 h-6 text-purple-400" />,
      title: "Auxiliaire de vie",
      description: "Aider les personnes dans leur quotidien"
    },
    {
      icon: <Shield className="w-6 h-6 text-cyan-400" />,
      title: "Agent de service hospitalier",
      description: "Assurer l'entretien en milieu hospitalier"
    }
  ];

  const avantages = [
    {
      icon: <Smile className="w-6 h-6 text-yellow-400" />,
      title: "Métiers valorisants",
      description: "Des professions au service des autres, riches en relations humaines"
    },
    {
      icon: <GraduationCap className="w-6 h-6 text-green-400" />,
      title: "Formation complète",
      description: "Un apprentissage théorique et pratique en situation réelle"
    },
    {
      icon: <Award className="w-6 h-6 text-orange-400" />,
      title: "Secteur porteur",
      description: "De nombreuses opportunités d'emploi dans un secteur en croissance"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen py-40 bg-gradient-to-br from-slate-900 to-slate-800"
    >
      {/* Scène 3D en arrière-plan */}
      <div className="fixed inset-0 z-0">
        <Scene3D />
      </div>
      
      <Loader />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Bouton retour */}
        <Link to="/">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mb-8 px-6 py-3 bg-slate-800/60 backdrop-blur-sm text-white rounded-lg flex items-center space-x-2 hover:bg-slate-700/60 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l'accueil</span>
          </motion.button>
        </Link>

        {/* En-tête */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <Heart className="w-16 h-16 text-pink-400" />
            <h1 className="text-5xl font-bold text-white">Pôle Services à la Personne</h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Formez-vous aux métiers de l'éducation, de l'aide et du service auprès des enfants et des adultes
          </p>
        </motion.div>

        {/* Formations */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Nos Formations</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {formations.map((formation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
                className="bg-slate-800/60 backdrop-blur-sm p-8 rounded-lg border-l-4 border-pink-500 hover:shadow-lg hover:shadow-pink-500/20 transition-all"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    {formation.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold text-white mb-2">{formation.title}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="px-3 py-1 bg-pink-600/30 text-pink-300 rounded-full text-sm border border-pink-400/30">
                        {formation.duration}
                      </span>
                      <span className="px-3 py-1 bg-purple-600/30 text-purple-300 rounded-full text-sm border border-purple-400/30">
                        {formation.niveau}
                      </span>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-6">{formation.description}</p>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-white flex items-center space-x-2">
                    <BookOpen className="w-5 h-5 text-pink-400" />
                    <span>Compétences développées :</span>
                  </h4>
                  <ul className="space-y-2">
                    {formation.competences.map((comp, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-gray-300">
                        <span className="text-pink-400 mt-1">•</span>
                        <span>{comp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Débouchés */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Débouchés Professionnels</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {debouches.map((debouche, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-slate-800/60 backdrop-blur-sm p-6 rounded-lg text-center hover:shadow-lg hover:shadow-pink-500/20 transition-all"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    {debouche.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{debouche.title}</h3>
                <p className="text-gray-300 text-sm">{debouche.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Avantages */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl font-bold text-white mb-10 text-center">Pourquoi choisir nos formations ?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {avantages.map((avantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-slate-800/60 backdrop-blur-sm p-6 rounded-lg text-center hover:shadow-lg hover:shadow-pink-500/20 transition-all"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    {avantage.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{avantage.title}</h3>
                <p className="text-gray-300 text-sm">{avantage.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-center"
        >
          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-pink-600 hover:bg-pink-700 text-white rounded-lg transition-colors text-lg font-semibold"
            >
              Nous contacter pour plus d'informations
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ServicesPersonne;